import { useCallback } from 'react';
import { setPlayerTags } from 'js/hooks/user/server';
import { useGetErrorMessage } from 'js/hooks/server/hooks';
import { useComponentConfig } from 'js/hooks/component/useComponentConfig';
import { useComponentTranslations } from 'js/hooks/component/useComponentTranslations';

import * as actions from '../store/actions';
import { useDynamicReducerDispatch } from '../store';
import { initiateDocumentUploadRequest, uploadFile } from '../server';
import * as Types from '../types';

export const useUploadDocuments = (documentGroup: Types.IDocumentGroupConfig) => {
    const translate = useComponentTranslations();
    const getInitiateDocumentUploadErrorMessage = useGetErrorMessage({
        tkPrefix: 'playtech.mobile.identt-manual.initiateDocumentUpload.'
    });
    const getUploadedDocumentUrlErrorMessage = useGetErrorMessage({
        tkPrefix: 'playtech.mobile.identt-manual.uploadedDocumentUrl.'
    });
    const getSetPlayerTagErrorMessage = useGetErrorMessage({
        tkPrefix: 'playtech.mobile.identt-manual.setPlayerTag.'
    });

    const dispatch = useDynamicReducerDispatch();
    const { clientHost } = useComponentConfig();
    const { playerTag } = documentGroup;

    return useCallback(async (
        formValues: Types.IDocumentGroupFormValues,
        files: Types.IExtendedFile[]
    ): Promise<string | undefined> => {
        let initiateDocumentUploadResponse;
        const defaultErrorMessage = translate('upload.error', translate('playtech.mobile.system.systemError'));

        try {
            initiateDocumentUploadResponse = await initiateDocumentUploadRequest();
        } catch (error) {
            return getInitiateDocumentUploadErrorMessage(error, defaultErrorMessage);
        }

        try {
            const { data: { accessToken, reference } } = initiateDocumentUploadResponse;
            const url = new URL(clientHost);
            url.pathname = `/api/v2/documents/${reference}/attachments/`;

            await Promise.all(files.map(file => uploadFile({ url: url.toString(), file, accessToken, reference })));
        } catch (error) {
            return getUploadedDocumentUrlErrorMessage(error, defaultErrorMessage);
        }

        if (!playerTag) {
            return;
        }

        try {
            const tag = {
                type: 'text',
                name: playerTag,
                value: [
                    formValues.documentType,
                    formValues.documentNumber,
                    formValues.noExpDate ? '' : formValues.documentExpDate
                ].join(',')
            };

            await setPlayerTags([tag]);

            dispatch(actions.setPlayerTags([tag]));
        } catch (error) {
            return getSetPlayerTagErrorMessage(error, defaultErrorMessage);
        }
    }, [
        clientHost, playerTag, translate, dispatch,
        getInitiateDocumentUploadErrorMessage, getUploadedDocumentUrlErrorMessage, getSetPlayerTagErrorMessage
    ]);
};
